import { Injectable } from '@angular/core';
import { BaseResponse, BaseService } from 'src/app/core/services/base.service';
import { HttpClient } from '@angular/common/http';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
    GetTournamentByIdResponse,
    GetTournamentCategoriesResponse,
    GetTournamentPointsResponse,
    TournamentCategorySubscription,
} from './tournament.interface';

@Injectable({
    providedIn: 'root',
})
export class TournamentService extends BaseService {
    constructor(private http: HttpClient) {
        super();
    }

    getAllTournaments(filters = {}) {
        const body = this.setBody();

        if (filters !== null) {
            Object.keys(filters).forEach((filter) => {
                if (filters[filter] !== null && filters[filter] !== '') {
                    if (filters[filter] instanceof Array) {
                        body.set(filter, `[${filters[filter].join(',')}]`);
                    } else {
                        body.set(filter, filters[filter]);
                    }
                }
            });

            body.set('loadAll', 1);
        }

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournaments_all, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    getTournaments(departmentType, filters): Observable<any> {
        // Instacia o body através de BaseSerive.setBody();
        let body = this.setBody();

        // Inicializar o id do departamento selecionado
        body.set('departmentType', departmentType);

        if (filters !== null) {
            Object.keys(filters).forEach((filter) => {
                if (filters[filter] !== null && filters[filter] !== '') {
                    if (filters[filter] instanceof Array) {
                        body.set(filter, `[${filters[filter].join(',')}]`);
                    } else {
                        body.set(filter, filters[filter]);
                    }
                }
            });
        }

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournaments, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    getTournamentByID(tournamentId: string, department: string) {
        let body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('departmentType', department);

        return this.http
            .post<BaseResponse<GetTournamentByIdResponse>>(
                this.urlService + this.routeAdress.route_tournament_detail,
                body.toString()
            )
            .pipe(shareReplay(1), catchError(this.serviceError));
    }

    getTournamentPoints(tournamentId: string, categoryId: string) {
        const body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('categoryId', categoryId);

        return this.http.post<BaseResponse<GetTournamentPointsResponse>>(
            this.urlService + this.routeAdress.route_tournament_points,
            body.toString()
        );
    }

    getTournamentCategories(tournamentId: string, department: string) {
        const body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('departmentType', department);

        return this.http
            .post<BaseResponse<GetTournamentCategoriesResponse>>(
                this.urlService + this.routeAdress.route_tournament_categories,
                body.toString()
            )
            .pipe(shareReplay(1), catchError(this.serviceError));
    }

    getSubscriptionsToCancel(tournamentId: string, department: string) {
        const body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('departmentType', department);

        return this.http
            .post<BaseResponse<TournamentCategorySubscription[]>>(
                this.urlService + this.routeAdress.route_tournament_subscription_category_cancel,
                body.toString()
            )
            .pipe(catchError(this.serviceError));
    }

    getEnrollments(tournamentId, categoryId) {
        let body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('categoryId', categoryId);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_subscription_category, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    confirmCategoryEnrollment(tournamentId, categoryId, partnerId?) {
        let body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('categoryId', categoryId);
        if (partnerId) {
            body.set('partnerId', partnerId);
        }

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_subscription_confirm, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    searchPartner(keyword) {
        let body = this.setBody();

        body.set('search', keyword);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_user_search, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    enroll(tournamentId, categoryId, partnerId?) {
        let body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('categoryId', categoryId);
        if (partnerId) body.set('partnerId', partnerId);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_subscription_insert, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    cancelEnrollment(tournamentId, categoryId, subscriptionId, code?) {
        let body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('categoryId', categoryId);
        body.set('subscriptionId', subscriptionId);
        if (code) body.set('confirmationCode', code);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_subscription_cancel, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    getGames(tournamentId, categoryId) {
        let body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('categoryId', categoryId);

        let response = this.http.post(this.urlService + this.routeAdress.route_tournament_game, body.toString()).pipe(
            map(this.extractData),
            map((response) => {
                if (!response.registers) return response.registers;
                /* AJUSTA ESTRUTURA INPUT */
                const [game] = response?.registers;
                let games, estrutura;

                if (game.caixas.length > 1) {
                    games = game.caixas;
                    estrutura = 2;
                } else {
                    const [caixa] = game.caixas;
                    games = caixa.dados_caixa;
                    estrutura = Number(caixa.tipo_calculo);
                }

                /* AJUSTA ESTRUTURA PARTICIPANTES */
                return games.map((phase) => {
                    // if (!phase.dados_sub_caixa) return { ...phase, estrutura };
                    let dados_sub_caixa;
                    if (phase.dados_caixa) {
                        //console.log(phase.dados_caixa);
                        phase.dados_caixa.map((caixa) => {
                            if (caixa.dados_sub_caixa) {
                                caixa.dados_sub_caixa = caixa.dados_sub_caixa.map((game) => {
                                    const participantGroup = Object.entries(game.participantes).reduce(
                                        (acc, item: any) => {
                                            // cada key é um lado. ex: [ 1: [], 2: [] ].. 1 e 2 são 'lados'
                                            // o valor dessa key é o array de participantes
                                            const [lado, arrParticipants] = item;

                                            const detailedResults = game.resultado_detalhado,
                                                results = game.resultado;

                                            let participantResults;
                                            if (detailedResults) {
                                                const [lado1Results, lado2Results] = Object.values(detailedResults);
                                                participantResults =
                                                    lado === '1'
                                                        ? Object.values(lado1Results).flat()
                                                        : Object.values(lado2Results).flat();
                                            } else {
                                                participantResults = Array.from(results[lado]);
                                            }

                                            // precisamos do lado para marcar quem é o campeão
                                            // pois api retorna `lado_vencedor: 2`
                                            // vamos salvar no objeto do participante

                                            const newObjStructure = {
                                                duplas: Boolean(arrParticipants.length > 1),
                                                lado: Number(lado),
                                                participants: arrParticipants,
                                                result: game.lado_vencedor ? participantResults : [], //adiciona também resultado ao objeto do participante
                                            };

                                            acc.push(newObjStructure);
                                            return acc;
                                        },
                                        []
                                    );
                                    const newObj = {
                                        participantGroup,
                                        ...game,
                                    };

                                    return newObj;
                                });
                            }
                            return caixa;
                        });
                    } else {
                        dados_sub_caixa = phase.dados_sub_caixa.map((game) => {
                            const participantGroup = Object.entries(game.participantes).reduce((acc, item: any) => {
                                // cada key é um lado. ex: [ 1: [], 2: [] ].. 1 e 2 são 'lados'
                                // o valor dessa key é o array de participantes
                                const [lado, arrParticipants] = item;

                                const detailedResults = game.resultado_detalhado,
                                    results = game.resultado;

                                let participantResults;
                                if (detailedResults) {
                                    const [lado1Results, lado2Results] = Object.values(detailedResults);
                                    participantResults =
                                        lado === '1'
                                            ? Object.values(lado1Results).flat()
                                            : Object.values(lado2Results).flat();
                                } else {
                                    participantResults = Array.from(results[lado]);
                                }

                                // precisamos do lado para marcar quem é o campeão
                                // pois api retorna `lado_vencedor: 2`
                                // vamos salvar no objeto do participante

                                const newObjStructure = {
                                    duplas: Boolean(arrParticipants.length > 1),
                                    lado: Number(lado),
                                    participants: arrParticipants,
                                    result: game.lado_vencedor ? participantResults : [], //adiciona também resultado ao objeto do participante
                                };

                                acc.push(newObjStructure);
                                return acc;
                            }, []);
                            const newObj = {
                                participantGroup,
                                ...game,
                            };

                            return newObj;
                        });
                    }

                    return {
                        ...phase,
                        dados_sub_caixa,
                        estrutura,
                    };
                });
            }),
            catchError(this.serviceError)
        );

        return response;
    }

    getGamesByBox(gameId) {
        let body = this.setBody();

        body.set('gameId', JSON.stringify(gameId));

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_game_bybox, body.toString())
            .pipe(
                map(this.extractData),
                map((response) => {
                    response.registers.arrDados = response.registers.arrDados.map((game) => {
                        const participantGroup = Object.entries(game.participantes).reduce((acc, item: any) => {
                            // cada key é um lado. ex: [ 1: [], 2: [] ].. 1 e 2 são 'lados'
                            // o valor dessa key é o array de participantes
                            const [lado, arrParticipants] = item;

                            const detailedResults = game.resultado_detalhado,
                                results = game.resultado;

                            let participantResults;
                            if (detailedResults) {
                                const [lado1Results, lado2Results] = Object.values(detailedResults);
                                participantResults =
                                    lado === '1'
                                        ? Object.values(lado1Results).flat()
                                        : Object.values(lado2Results).flat();
                            } else {
                                participantResults = Array.from(results[lado]);
                            }

                            // precisamos do lado para marcar quem é o campeão
                            // pois api retorna `lado_vencedor: 2`
                            // vamos salvar no objeto do participante

                            const newObjStructure = {
                                duplas: Boolean(arrParticipants.length > 1),
                                lado: Number(lado),
                                participants: arrParticipants,
                                result: game.lado_vencedor ? participantResults : [], //adiciona também resultado ao objeto do participante
                            };

                            acc.push(newObjStructure);
                            return acc;
                        }, []);
                        const newObj = {
                            participantGroup,
                            ...game,
                        };

                        return newObj;
                    });
                    return response;
                }),
                catchError(this.serviceError)
            );

        return response;
    }

    getClassification(tournamentId, categoryId) {
        let body = this.setBody();

        body.set('tournamentId', tournamentId);
        body.set('categoryId', categoryId);

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_classification, body.toString())
            .pipe(map(this.extractData), catchError(this.serviceError));

        return response;
    }

    getMatches(tournamentId, categoryId, filters?) {
        const body = this.setBody();
        body.set('tournamentId', tournamentId);
        body.set('category', categoryId);
        if (filters) {
            Object.keys(filters).forEach((filter) => {
                if (filters[filter] != null && filters[filter] != '') {
                    if (filters[filter] instanceof Array) {
                        body.set(filter, `[${filters[filter].join(',')}]`);
                    } else {
                        body.set(filter, filters[filter]);
                    }
                }
            });
        }

        let response = this.http
            .post(this.urlService + this.routeAdress.route_tournament_program_category, body.toString())
            .pipe(
                map(this.extractData),
                map((response) => {
                    if (!response.registers.list) return response;
                    response.registers.list = response.registers.list.map((game) => {
                        const participantGroup = Object.entries(game.participantes).reduce((acc, item: any) => {
                            // cada key é um lado. ex: [ 1: [], 2: [] ].. 1 e 2 são 'lados'
                            // o valor dessa key é o array de participantes
                            const [lado, arrParticipants] = item;

                            const detailedResults = game.resultado_detalhado,
                                results = game.resultado;

                            let participantResults;
                            if (detailedResults) {
                                const [lado1Results, lado2Results] = Object.values(detailedResults);
                                participantResults =
                                    lado === '1'
                                        ? Object.values(lado1Results).flat()
                                        : Object.values(lado2Results).flat();
                            } else {
                                participantResults = results ? Array.from(results[lado]) : [];
                            }

                            // precisamos do lado para marcar quem é o campeão
                            // pois api retorna `lado_vencedor: 2`
                            // vamos salvar no objeto do participante

                            const newObjStructure = {
                                duplas: Boolean(arrParticipants.length > 1),
                                lado: Number(lado),
                                participants: arrParticipants,
                                result: game.lado_vencedor ? participantResults : [], //adiciona também resultado ao objeto do participante
                            };

                            acc.push(newObjStructure);
                            return acc;
                        }, []);
                        const newObj = {
                            participantGroup,
                            ...game,
                        };

                        return newObj;
                    });

                    return response;
                }),
                catchError(this.serviceError)
            );

        return response;
    }
}
